import { useCallback, useEffect, useState } from 'react';

export type FontOption = 'geist' | 'system' | 'trebuchet-ms';

interface FontConfig {
  id: FontOption;
  name: string;
  value: string;
  numericValue: string;
}

export const DEFAULT_FONT: FontOption = 'geist';

const SYSTEM_MONO_STACK = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';

const FONT_OPTIONS: FontConfig[] = [
  {
    id: 'geist',
    name: 'Geist',
    value: '"Geist", -apple-system, "system-ui", sans-serif',
    numericValue: `"Geist", ${SYSTEM_MONO_STACK}`,
  },
  {
    id: 'trebuchet-ms',
    name: 'Trebuchet MS',
    value: '"Trebuchet MS", -apple-system, "system-ui", sans-serif',
    numericValue: SYSTEM_MONO_STACK,
  },
  {
    id: 'system',
    name: 'System Default',
    value: '-apple-system, "system-ui", "Trebuchet MS", Roboto, Ubuntu, sans-serif',
    numericValue: SYSTEM_MONO_STACK,
  },
];

const FONT_STORAGE_KEY = 'interface-font-v2';

export function useFontSelection() {
  const [selectedFont, setSelectedFont] = useState<FontOption>(() => {
    try {
      const saved = localStorage.getItem(FONT_STORAGE_KEY) as FontOption;
      if (FONT_OPTIONS.some((f) => f.id === saved)) return saved;
    } catch {
      // localStorage accessors throw when the browser blocks site data — fall back to the default.
    }
    return DEFAULT_FONT;
  });

  const applyFont = useCallback((fontOption: FontOption) => {
    const fontConfig = FONT_OPTIONS.find((f) => f.id === fontOption);
    if (!fontConfig) return;

    document.documentElement.style.setProperty('--main-font-family', fontConfig.value);
    document.documentElement.style.setProperty('--numeric-font-family', fontConfig.numericValue);
    try {
      localStorage.setItem(FONT_STORAGE_KEY, fontOption);
    } catch {
      // Storage may be blocked; the font still applies for this session.
    }
  }, []);

  const handleFontChange = useCallback(
    (fontOption: FontOption) => {
      setSelectedFont(fontOption);
      applyFont(fontOption);
    },
    [applyFont]
  );

  const resetFont = useCallback(() => {
    handleFontChange(DEFAULT_FONT);
  }, [handleFontChange]);

  useEffect(() => {
    applyFont(selectedFont);
  }, [selectedFont, applyFont]);

  return {
    selectedFont,
    fontOptions: FONT_OPTIONS,
    handleFontChange,
    resetFont,
  };
}
